/**
 * align-math.ts — exact similarity transform (uniform scale + rotation +
 * translation) from corresponding point pairs, and its composition onto a
 * splat's existing transform.
 *
 * This is the closed-form answer to "put THIS capture where THAT one is":
 * click the same 3 features in both clouds and the transform is determined —
 * no search, no scale hypotheses, no fitness scoring. Three pairs is the
 * minimum (a triangle fixes orientation, and its edge-length ratio fixes
 * scale); more pairs are accepted and solved in the least-squares sense.
 *
 * Rotation comes from Horn's quaternion method (the eigenvector of the 4x4
 * profile matrix N with the largest eigenvalue), scale from Umeyama. Both are
 * standard and, unlike an SVD-based Kabsch, need no external matrix library.
 *
 * Intentionally free of playcanvas/DOM imports so it stays unit-testable
 * offline — see `tools/align-math-test.mjs`.
 */

export type V3 = [number, number, number];
/** Quaternion as [x, y, z, w] — the order playcanvas's Quat.set takes. */
export type Quat4 = [number, number, number, number];

export interface Similarity {
    scale: number;
    rotation: Quat4;
    position: V3;
    /** RMS of |S(src_i) − dst_i| in destination units — how well the picks agree. */
    rmse: number;
    /** True when the source picks are (near-)collinear, so the rotation about
     *  that line is unconstrained and the result must not be trusted. */
    degenerate: boolean;
}

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: V3, b: V3): V3 => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
];
const len = (a: V3): number => Math.hypot(a[0], a[1], a[2]);

const centroid = (pts: V3[]): V3 => {
    const c: V3 = [0, 0, 0];
    for (const p of pts) { c[0] += p[0]; c[1] += p[1]; c[2] += p[2]; }
    return [c[0] / pts.length, c[1] / pts.length, c[2] / pts.length];
};

/** Rotation matrix (row-major 3x3) from a quaternion [x, y, z, w]. */
export const quatToMat3 = (q: Quat4): number[] => {
    const [x, y, z, w] = q;
    return [
        1 - 2 * (y * y + z * z), 2 * (x * y - z * w), 2 * (x * z + y * w),
        2 * (x * y + z * w), 1 - 2 * (x * x + z * z), 2 * (y * z - x * w),
        2 * (x * z - y * w), 2 * (y * z + x * w), 1 - 2 * (x * x + y * y)
    ];
};

const matMulVec = (R: number[], v: V3): V3 => [
    R[0] * v[0] + R[1] * v[1] + R[2] * v[2],
    R[3] * v[0] + R[4] * v[1] + R[5] * v[2],
    R[6] * v[0] + R[7] * v[1] + R[8] * v[2]
];

/** Hamilton product a·b, both [x, y, z, w]; applies b first, then a. */
export const quatMul = (a: Quat4, b: Quat4): Quat4 => {
    const [ax, ay, az, aw] = a;
    const [bx, by, bz, bw] = b;
    return [
        aw * bx + ax * bw + ay * bz - az * by,
        aw * by - ax * bz + ay * bw + az * bx,
        aw * bz + ax * by - ay * bx + az * bw,
        aw * bw - ax * bx - ay * by - az * bz
    ];
};

/** Rotate a vector by a quaternion. */
export const quatRotate = (q: Quat4, v: V3): V3 => matMulVec(quatToMat3(q), v);

/**
 * Largest-eigenvalue eigenvector of a symmetric 4x4, by cyclic Jacobi
 * rotations — direct and quadratically convergent.
 *
 * (Power iteration was tried first and is wrong for this job: making the
 * matrix positive definite needs a shift as large as the spectral radius,
 * which drives the eigenvalue ratio toward 1 and stalls convergence. The unit
 * test caught it as up to 34° of rotation error on random inputs.)
 */
const dominantEigenvector4 = (N: number[][]): number[] => {
    const n = 4;
    const A = N.map(row => row.slice());
    const V = [[1, 0, 0, 0], [0, 1, 0, 0], [0, 0, 1, 0], [0, 0, 0, 1]];
    for (let sweep = 0; sweep < 60; sweep++) {
        let off = 0;
        for (let p = 0; p < n; p++) {
            for (let q = p + 1; q < n; q++) off += A[p][q] * A[p][q];
        }
        if (off < 1e-30) break;
        for (let p = 0; p < n; p++) {
            for (let q = p + 1; q < n; q++) {
                if (Math.abs(A[p][q]) < 1e-300) continue;
                const theta = (A[q][q] - A[p][p]) / (2 * A[p][q]);
                const sign = theta >= 0 ? 1 : -1;
                const t = sign / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
                const c = 1 / Math.sqrt(t * t + 1);
                const s = t * c;
                for (let k = 0; k < n; k++) {          // A · J
                    const akp = A[k][p], akq = A[k][q];
                    A[k][p] = c * akp - s * akq;
                    A[k][q] = s * akp + c * akq;
                }
                for (let k = 0; k < n; k++) {          // Jᵀ · (A · J)
                    const apk = A[p][k], aqk = A[q][k];
                    A[p][k] = c * apk - s * aqk;
                    A[q][k] = s * apk + c * aqk;
                }
                for (let k = 0; k < n; k++) {          // V · J
                    const vkp = V[k][p], vkq = V[k][q];
                    V[k][p] = c * vkp - s * vkq;
                    V[k][q] = s * vkp + c * vkq;
                }
            }
        }
    }
    let bi = 0;
    for (let i = 1; i < n; i++) if (A[i][i] > A[bi][bi]) bi = i;
    return [V[0][bi], V[1][bi], V[2][bi], V[3][bi]];
};

/** Twice the triangle area spanned by the first three points (0 = collinear). */
const spread = (pts: V3[]): number => {
    if (pts.length < 3) return 0;
    return len(cross(sub(pts[1], pts[0]), sub(pts[2], pts[0])));
};

/**
 * The similarity S with S(src_i) ≈ dst_i: uniform scale, rotation, translation.
 * Needs at least 3 pairs. Returns null if fewer, or if the geometry is
 * degenerate enough that no rotation is determined.
 */
export const similarityFromPairs = (src: V3[], dst: V3[]): Similarity | null => {
    const n = Math.min(src.length, dst.length);
    if (n < 3) return null;

    const cs = centroid(src.slice(0, n));
    const cd = centroid(dst.slice(0, n));
    const p = src.slice(0, n).map(v => sub(v, cs));
    const q = dst.slice(0, n).map(v => sub(v, cd));

    // Collinear picks leave the rotation about that line free — refuse rather
    // than return a confidently wrong transform.
    const maxEdge = Math.max(
        len(sub(src[1], src[0])), len(sub(src[2], src[0])), len(sub(src[2], src[1]))
    );
    const degenerate = maxEdge < 1e-9 || spread(src.slice(0, n)) < 1e-3 * maxEdge * maxEdge;

    // Cross-covariance.
    let Sxx = 0, Sxy = 0, Sxz = 0, Syx = 0, Syy = 0, Syz = 0, Szx = 0, Szy = 0, Szz = 0;
    let varP = 0;
    for (let i = 0; i < n; i++) {
        const [px, py, pz] = p[i];
        const [qx, qy, qz] = q[i];
        Sxx += px * qx; Sxy += px * qy; Sxz += px * qz;
        Syx += py * qx; Syy += py * qy; Syz += py * qz;
        Szx += pz * qx; Szy += pz * qy; Szz += pz * qz;
        varP += px * px + py * py + pz * pz;
    }
    if (varP < 1e-12) return null;

    // Horn's profile matrix; its dominant eigenvector is the optimal rotation
    // quaternion in (w, x, y, z) order.
    const N = [
        [Sxx + Syy + Szz, Syz - Szy, Szx - Sxz, Sxy - Syx],
        [Syz - Szy, Sxx - Syy - Szz, Sxy + Syx, Szx + Sxz],
        [Szx - Sxz, Sxy + Syx, -Sxx + Syy - Szz, Syz + Szy],
        [Sxy - Syx, Szx + Sxz, Syz + Szy, -Sxx - Syy + Szz]
    ];
    const [qw, qx, qy, qz] = dominantEigenvector4(N);
    const rotation: Quat4 = [qx, qy, qz, qw];
    const R = quatToMat3(rotation);

    // Umeyama scale.
    let num = 0;
    for (let i = 0; i < n; i++) {
        const rp = matMulVec(R, p[i]);
        num += q[i][0] * rp[0] + q[i][1] * rp[1] + q[i][2] * rp[2];
    }
    const scale = num / varP;

    const rc = matMulVec(R, cs);
    const position: V3 = [
        cd[0] - scale * rc[0],
        cd[1] - scale * rc[1],
        cd[2] - scale * rc[2]
    ];

    // Residual of the fit in destination units.
    let sum = 0;
    for (let i = 0; i < n; i++) {
        const m = matMulVec(R, src[i]);
        const dx = scale * m[0] + position[0] - dst[i][0];
        const dy = scale * m[1] + position[1] - dst[i][1];
        const dz = scale * m[2] + position[2] - dst[i][2];
        sum += dx * dx + dy * dy + dz * dz;
    }
    return { scale, rotation, position, rmse: Math.sqrt(sum / n), degenerate };
};

/**
 * Distance from each transformed source point to its destination partner.
 *
 * With only 3 pairs a similarity has just 2 degrees of freedom left over, so
 * these numbers are a weak check; with 4+ pairs they become the real quality
 * signal — one bad pick stands out as a single large residual while the others
 * stay small, which is how you find the pick to redo.
 */
export const pairResiduals = (sim: Similarity, src: V3[], dst: V3[]): number[] => {
    const R = quatToMat3(sim.rotation);
    const n = Math.min(src.length, dst.length);
    const out: number[] = [];
    for (let i = 0; i < n; i++) {
        const m = matMulVec(R, src[i]);
        out.push(Math.hypot(
            sim.scale * m[0] + sim.position[0] - dst[i][0],
            sim.scale * m[1] + sim.position[1] - dst[i][1],
            sim.scale * m[2] + sim.position[2] - dst[i][2]
        ));
    }
    return out;
};

export interface Transform {
    position: V3;
    rotation: Quat4;
    scale: V3;
}

/**
 * Apply a world-space similarity to an object that already has a transform.
 *
 * The picks are made in WORLD space, so S maps the splat's current world
 * position to its target world position. Its local→world transform must be
 * post-multiplied by S, not replaced: local → world → world'.
 */
export const composeTransform = (sim: Similarity, cur: Transform): Transform => {
    const rotated = quatRotate(sim.rotation, cur.position);
    return {
        position: [
            sim.scale * rotated[0] + sim.position[0],
            sim.scale * rotated[1] + sim.position[1],
            sim.scale * rotated[2] + sim.position[2]
        ],
        rotation: quatMul(sim.rotation, cur.rotation),
        scale: [cur.scale[0] * sim.scale, cur.scale[1] * sim.scale, cur.scale[2] * sim.scale]
    };
};
